import { useState, useEffect } from "react"
import { useSelector, useDispatch, shallowEqual } from "react-redux"
import { DateTime } from "luxon"
import Button from "react-bootstrap/Button"
import { FiAlertTriangle } from "react-icons/fi"

import "./index.scss"
import useT from "utils/hooks/useT"
import { Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import { canSign } from "state_management/lims/segregationOfDuties"
import { fetch as fetchCommentTemplates, selectByContext } from "state_management/lims/limsCommentLibrary"

// Reusable Part 11 electronic-signature dialog: re-authenticate (password) + state a
// meaning (reason). Enforces segregation of duties (capability + 4-eyes) when given those
// props — as a fail-fast UX gate; the BACKEND re-verifies the password, capability and
// 4-eyes on every signing endpoint and 403s on any failed gate (SPEC-021 B1).
//
// Password handling (SPEC-021 B1): the password is held in component memory only — never
// in redux and never in localStorage. It is passed to `onSign` inside the payload so the
// calling thunk can place it at the TOP LEVEL of the signing request body (the backend
// verifies + discards it); the field is cleared after a successful sign and on unmount.
//
// `onSign` may return a promise: the dialog then shows a busy state and, on rejection,
// surfaces the error (e.g. the backend's 403 "Password verification failed" /
// capability / 4-eyes message) in an inline error panel and stays open so the signer can
// correct the password. The CALLER closes the dialog on success (by unmounting it).
// `requireReason` (PRD-006 K13, default off — non-invasive): the QA-correction flows (amend
// decision / lift hold / reopen OOS) demand a mandatory justification. When set, the reason field
// is required and the dialog refuses to sign with a blank reason — a fail-fast mirror of the server,
// which is authoritative (422 on a blank reason). All existing callers leave it off and are unaffected.
// `commentContext` (F1, SPEC-033, default null — non-invasive): when set to a signature context
// (reviewer / supervisor / qa-release / return-for-correction / oos-conclusion), a "Standard
// comment" dropdown is rendered ABOVE the reason textarea, listing the team's canned comments for
// that context; picking one PRE-FILLS the reason (still freely editable). With no prop the dropdown
// is absent and the dialog is byte-for-byte the old one — fully back-compatible.
//
// RR8 #4053/#4054 — THIS DIALOG ONLY SIGNS. It used to render an "Action" dropdown built from a
// `reasons` prop, so the meaning it displayed could differ from the action the user had picked in
// the menu. Worse, the pick was inert: `sign()` sets `LimsSignature.meaning` server-side per route,
// `_signature_ref()` persists only {signatureId, memberId, initials, reason, timestamp}, and
// `_strip_seeded_signatures()` discards any client-posted signature object — so the chosen value
// was never stored anywhere. The dropdown and the `reasons` prop are therefore gone entirely rather
// than replaced by a fixed value: keeping a field nobody reads only implies that it matters.
// #4141 — capability id -> its translated label, so the blocked panel can NAME the capability the
// action needs. Mirrors the labels used by MemberConfig and the QA team page.
const CAPABILITY_LABEL_KEYS = {
  analyst: "limsCapAnalyst",
  reviewer: "limsCapReviewer",
  supervisor: "limsCapSupervisor",
  qa: "limsCapQa",
  "qa-manager": "limsCapQaManager",
}

export default function ESignatureModal({
  title,
  requiredCapability = null,
  excludedMemberIds = [],
  requireReason = false,
  commentContext = null,
  onSign,
  onCancel,
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const memberId = useSelector((state) => state.auth.memberId)
  const initials = useSelector((state) => (state.members[memberId] && state.members[memberId].initials) || "")
  const gate = useSelector(
    (state) => canSign(state, { capability: requiredCapability, excludedMemberIds }),
    shallowEqual
  )
  // #4141 — the detail line used to be a FIXED four-eyes explanation printed under whatever the
  // reason said. So a signer refused for the wrong ROLE read "because you entered or reviewed this
  // result, you may not also sign it" — the reason line said capability, the detail contradicted it,
  // and the detail is the line people read as the explanation. It now follows the reason, and the
  // capability case names the capability actually required instead of leaving the reader to guess.
  const blockedDetail =
    gate.reasonKey === "limsEsigBlockedCapability"
      ? t("limsEsigBlockedCapabilityDetail", {
          capability: requiredCapability ? t(CAPABILITY_LABEL_KEYS[requiredCapability] || "") : "",
        })
      : t("limsEsigBlockedDetail")

  // Canned standard comments for this context (empty unless `commentContext` is set).
  const commentTemplates = useSelector(
    (state) => (commentContext ? selectByContext(state, commentContext) : []),
    shallowEqual
  )

  // Lazily load the comment-library when a context is requested. The modal renders in several
  // places (analysis task, Sample View, LIMS screens), not all of which pre-fetch the slice, so
  // it self-fetches — cheap, team-scoped, and a no-op when `commentContext` is unset. An empty
  // result just means no dropdown (back-compatible).
  useEffect(() => {
    if (commentContext) dispatch(fetchCommentTemplates())
  }, [dispatch, commentContext])

  // The free-text is the REASON (justification) recorded on the audit entry. There is no
  // action/meaning field: the server sets the signature meaning per route (RR8 #4053/#4054).
  const [reason, setReason] = useState("")
  const [password, setPassword] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // K13: with requireReason on, a blank reason blocks the sign (fail-fast; the server 422s too).
  const reasonMissing = requireReason && !reason.trim()

  const handleSign = (e) => {
    if (e) e.preventDefault()
    if (!password || busy) return
    if (reasonMissing) {
      setError(t("limsEsigReasonRequired"))
      return
    }
    setError(null)
    const result = onSign({
      memberId,
      initials,
      reason: reason || null,
      timestamp: DateTime.now().toISO(),
      // In memory only — the consuming thunk lifts it onto the request body top level
      // and never persists it (redux/localStorage) nor embeds it in stored payloads.
      password,
    })
    if (result && typeof result.then === "function") {
      setBusy(true)
      result
        .then(() => setPassword(""))
        .catch((err) => setError((err && err.message) || t("limsEsigFailed")))
        .finally(() => setBusy(false))
    } else {
      setPassword("")
    }
  }

  return (
    <Popup nonScrollable title={title || t("limsEsigTitle")} onCancel={onCancel}>
      {!gate.allowed ? (
        <div className="LimsEsig-blocked">
          <div className="LimsEsig-blocked-panel">
            <FiAlertTriangle className="LimsEsig-blocked-icon" />
            <div className="LimsEsig-blocked-body">
              <p className="LimsEsig-blocked-title">{t("limsEsigBlockedTitle")}</p>
              <p className="LimsEsig-blocked-reason">{t(gate.reasonKey)}</p>
              <p className="LimsEsig-blocked-detail">{blockedDetail}</p>
            </div>
          </div>
          <div className="LimsEsig-buttons">
            <Button variant="secondary" onClick={onCancel}>
              {t("close")}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="LimsEsig"
          onKeyDown={(event) => {
            // RR8 #4052: pressing Enter while signing closed the WHOLE analysis task. RR6 (below)
            // removed the nested <form>, but the fields still sit inside the OUTER analysis-task
            // form, and Enter in a single-line input triggers THAT form's implicit submission. The
            // guard therefore belongs here on the root, not per field — a field added later is
            // covered automatically. <textarea> is exempt so a reason can still contain newlines.
            // Enter deliberately does NOT sign: a GMP signature needs a real click.
            if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
        >
          {/* RR6: a plain <div>, NOT a <form>. This dialog is frequently rendered INSIDE another
              form (e.g. the analysis-task ConfigContainer form); a nested <form> is invalid HTML
              that made the "Sign" submit bubble into a native full-page reload — closing the whole
              analysis modal. Signing is driven by the button's onClick instead. */}
          {commentContext && commentTemplates.length > 0 && (
            <Container>
              <Title>{t("limsCommentStandard")}</Title>
              {/* Picking a standard comment PRE-FILLS the reason (still editable). The Select is
                  not bound to a stored value — it is a one-shot picker that writes into `reason`. */}
              <Select
                isMulti={false}
                collection={commentTemplates.map((tpl) => ({ id: tpl.id, name: tpl.text }))}
                value=""
                onChange={(v) => {
                  const picked = commentTemplates.find((tpl) => tpl.id === (v[0] || ""))
                  if (picked) setReason(picked.text)
                }}
                menuPlacement="bottom"
              />
              <p className="LimsEsig-help LimsEsig-comment-help">{t("limsCommentStandardHint")}</p>
            </Container>
          )}
          <Container>
            <Title>{requireReason ? `${t("limsEsigReason")} *` : t("limsEsigReason")}</Title>
            <textarea
              className="LimsEsig-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {requireReason && <p className="LimsEsig-help">{t("limsEsigReasonRequiredHint")}</p>}
          </Container>
          <Container>
            <Title>{t("password")}</Title>
            <Text type="password" required value={password} onChange={setPassword} />
          </Container>
          <p className="LimsEsig-help">{t("limsEsigPasswordHelp")}</p>
          {error && (
            <div className="LimsEsig-blocked-panel">
              <FiAlertTriangle className="LimsEsig-blocked-icon" />
              <div className="LimsEsig-blocked-body">
                <p className="LimsEsig-blocked-title">{t("limsEsigFailed")}</p>
                <p className="LimsEsig-blocked-reason">{error}</p>
              </div>
            </div>
          )}
          <div className="LimsEsig-buttons">
            <Button variant="light" onClick={onCancel} disabled={busy}>
              {t("cancel")}
            </Button>
            <Button type="button" variant="primary" onClick={handleSign} disabled={busy || reasonMissing}>
              {t("limsEsigSign")}
            </Button>
          </div>
        </div>
      )}
    </Popup>
  )
}
